import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getFirestore, doc, updateDoc, setDoc, increment, query, collection, where, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";

// Анимация галочки
const checkmarkAnimation = keyframes`
    0% {
        stroke-dashoffset: 50;
    }
    100% {
        stroke-dashoffset: 0;
    }
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Стили для обертки
const Wrapper = styled.div`
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f8f9fa;
    text-align: center;
`;

// Стили для карточки успеха
const MessageBox = styled.div`
    box-shadow: 0 15px 25px #00000019;
    padding: 45px;
    text-align: center;
    margin: 40px auto;
    border-bottom: solid 4px ${({ success }) => (success ? "#28a745" : "red")};
    background: white;
    animation: ${fadeIn} 0.5s ease-out;
    width: 100%;
    max-width: 500px;
`;

// Стили для заголовка
const Title = styled.h2`
    font-size: 40px;
    font-weight: 500;
    line-height: 1.2;
    margin-top: 10px;
    margin-bottom: 12px;
    color: ${({ success }) => (success ? "#28a745" : "red")};
`;

// Стили для текста
const Subtitle = styled.p`
    margin-bottom: 0px;
    font-size: 18px;
    color: #495057;
    font-weight: 500;
`;

// Стили для иконки
const IconWrapper = styled.div`
    margin: 0 auto;
    width: 100px;
    height: 100px;
    position: relative;
`;

const CheckmarkCircle = styled.svg`
    width: 100px;
    height: 100px;
`;

const CheckmarkPath = styled.path`
    fill: none;
    stroke: ${({ success }) => (success ? "#28a745" : "red")};
    stroke-width: 4;
    stroke-dasharray: 50;
    stroke-dashoffset: 50;
    animation: ${checkmarkAnimation} 0.8s ease-out forwards;
`;

const Button = styled.button`
    background: ${({ success }) => (success ? "#28a745" : "red")};
    color: #ffffff;
    font-size: 1rem;
    font-weight: bold;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 20px;
    transition: all 0.3s ease;

    &:hover {
        background: ${({ success }) => (success ? "#218838" : "#c82333")};
        transform: scale(1.05);
    }
`;

// Компонент сообщения
const PaymentMessage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: parcelIdFromParams } = useParams();
    const parcelId = location.state?.parcelId || parcelIdFromParams || localStorage.getItem("currentParcelId");
    const [balanceUpdated, setBalanceUpdated] = useState(false);

    // Получаем параметры из URL для пополнения баланса
    const queryParams = new URLSearchParams(location.search);
    const invoiceId = queryParams.get('invoice');
    const amount = queryParams.get('amount');
    const userId = queryParams.get('userId');
    const status = queryParams.get('status');

    useEffect(() => {
        if (parcelId) {
            localStorage.setItem("currentParcelId", parcelId);
        }
    }, [parcelId]);

    // Обработка пополнения баланса
    useEffect(() => {
        console.log('🔄 PaymentSuccess useEffect triggered');
        console.log('📋 URL Parameters:', {
            invoiceId,
            amount,
            userId,
            status,
            balanceUpdated
        });

        const updateBalance = async () => {
            // Проверяем, что это пополнение баланса (а не оплата посылки)
            if (invoiceId && amount && userId && !balanceUpdated) {
                console.log('💰 Starting balance update process...');
                console.log('   Invoice ID:', invoiceId);
                console.log('   Amount:', amount, 'KZT');
                console.log('   User ID:', userId);

                try {
                    const db = getFirestore();

                    // Находим документ баланса по userId
                    const balanceQuery = query(
                        collection(db, 'balances'),
                        where('userId', '==', userId)
                    );

                    console.log('🔍 Searching for balance document...');
                    const balanceSnapshot = await getDocs(balanceQuery);
                    console.log('📊 Query result:', {
                        empty: balanceSnapshot.empty,
                        size: balanceSnapshot.size
                    });

                    if (!balanceSnapshot.empty) {
                        // Обновляем существующий баланс
                        const balanceDoc = balanceSnapshot.docs[0];
                        const currentBalanceData = balanceDoc.data();
                        console.log('📄 Found balance document:', {
                            documentId: balanceDoc.id,
                            currentBalance: currentBalanceData.balance,
                            userId: currentBalanceData.userId
                        });

                        await updateDoc(doc(db, 'balances', balanceDoc.id), {
                            balance: increment(parseFloat(amount))
                        });

                        console.log(`✅ Balance updated successfully!`);
                        console.log(`   Previous: ${currentBalanceData.balance} KZT`);
                        console.log(`   Added: +${amount} KZT`);
                        console.log(`   New: ${currentBalanceData.balance + parseFloat(amount)} KZT`);
                        console.log(`   Document ID: ${balanceDoc.id}`);
                    } else {
                        console.warn('⚠️ No balance document found, creating new...');
                        // Если документа баланса нет - создаем новый
                        const balanceRef = doc(collection(db, 'balances'));
                        await setDoc(balanceRef, {
                            userId: userId,
                            balance: parseFloat(amount),
                            createdAt: new Date().toISOString()
                        });

                        console.log(`✅ New balance created: ${amount} KZT for user ${userId}`);
                        console.log(`   New document ID: ${balanceRef.id}`);
                    }

                    // Обновляем localStorage для мгновенного отображения
                    const currentBalance = parseFloat(localStorage.getItem('balance') || '0');
                    const newBalance = currentBalance + parseFloat(amount);
                    console.log('💾 Updating localStorage:', {
                        previous: currentBalance,
                        added: parseFloat(amount),
                        new: newBalance
                    });
                    localStorage.setItem('balance', newBalance.toString());

                    setBalanceUpdated(true);
                    console.log('✅ Balance update completed!');
                } catch (error) {
                    console.error('❌ Error updating balance:', error);
                    console.error('   Error name:', error.name);
                    console.error('   Error message:', error.message);
                    console.error('   Error stack:', error.stack);
                }
            } else {
                console.log('⏭️ Skipping balance update:');
                if (!invoiceId) console.log('   ❌ No invoiceId');
                if (!amount) console.log('   ❌ No amount');
                if (!userId) console.log('   ❌ No userId');
                if (balanceUpdated) console.log('   ✅ Already updated');
            }
        };

        updateBalance();
    }, [invoiceId, amount, userId, balanceUpdated]);

    // ✅ Определяем успешность оплаты
    const success = location.state?.success === true || (invoiceId && status !== 'failed');

    const handleGoBack = () => {
        // Если это пополнение баланса
        if (invoiceId && amount) {
            navigate("/PersonalArea");
        }
        // Если это оплата посылки
        else if (success) {
            navigate("/IncomingParcels");
        } else if (parcelId) {
            navigate(`/SendParcelForm/${parcelId}`);
        } else {
            navigate("/SendParcelForm");
        }
    };

    return (
        <Wrapper>
            <MessageBox success={success}>
                <IconWrapper>
                    <CheckmarkCircle>
                        <circle cx="50" cy="50" r="48" fill="none" stroke="#ddd" strokeWidth="4" />
                        <CheckmarkPath
                            success={success}
                            d="M30 50 l15 15 l25 -25"
                        />
                    </CheckmarkCircle>
                </IconWrapper>
                <Title success={success}>
                    {success ? t('paymentSuccess.success') : t('paymentSuccess.failed')}
                </Title>
                <Subtitle>
                    {success
                        ? t('paymentSuccess.thankYou')
                        : t('paymentSuccess.tryAgainLater')}
                </Subtitle>
                <Button success={success} onClick={handleGoBack}>
                    {success ? t('paymentSuccess.returnToMain') : t('paymentSuccess.tryAgain')}
                </Button>
            </MessageBox>
        </Wrapper>
    );
};


export default PaymentMessage;